import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface NotificationPreferences {
  user_id: string;
  enabled_notification_types: string[];
  disabled_notification_types: string[];
  priority_threshold: 'low' | 'medium' | 'high' | 'critical';
  in_app_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  digest_enabled: boolean;
  digest_frequency: 'daily' | 'weekly';
  digest_day: string | null;
  digest_time: string;
  auto_mark_read_after_days: number;
  created_at: string;
  updated_at: string;
}

export const useNotificationPreferences = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      // Create default preferences if none exist
      if (!data) {
        const { data: newPrefs, error: insertError } = await supabase
          .from('notification_preferences')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) throw insertError;
        return newPrefs as NotificationPreferences;
      }

      return data as NotificationPreferences;
    },
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast({
        title: "Preferences updated",
        description: "Your notification preferences have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleNotificationType = useMutation({
    mutationFn: async ({ type, enabled }: { type: string; enabled: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!preferences) throw new Error('No preferences loaded');

      let enabledTypes = [...preferences.enabled_notification_types];
      let disabledTypes = [...preferences.disabled_notification_types];

      if (enabled) {
        if (!enabledTypes.includes(type)) {
          enabledTypes.push(type);
        }
        disabledTypes = disabledTypes.filter(t => t !== type);
      } else {
        enabledTypes = enabledTypes.filter(t => t !== type);
        if (!disabledTypes.includes(type)) {
          disabledTypes.push(type);
        }
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .update({
          enabled_notification_types: enabledTypes,
          disabled_notification_types: disabledTypes,
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    preferences,
    isLoading,
    updatePreferences: updatePreferences.mutate,
    toggleNotificationType: toggleNotificationType.mutate,
    isUpdating: updatePreferences.isPending || toggleNotificationType.isPending,
  };
};
